'use server';

import { MuiButton } from '@/components';
import { UseFirstRender } from '@/hooks';
import {
  inviteAccepted,
  loginApple,
  loginFacebook,
  loginGoogle,
  loginUserView,
  openModal,
  registerUser,
  registerUserView,
  setGuest,
} from '@/store/actions';
import { authSelector } from '@/store/selectors';
import { palette } from '@/theme/constants';
import { Box, CircularProgress, Divider, Grid, IconButton, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormikConfig } from './formik';
import { useGoogleLogin } from '@react-oauth/google';
import { face_client_id, google_client_id } from '@/utils';

import AppleLogin from 'react-apple-login';
import EmailPasswordStep from './components/EmailPasswordStep';
import UserDetailsStep from './components/UserDetailsStep';
import { useAuth } from '@/hooks/authContext';
import Link from 'next/link';
import WalkThrough from '@/components/Walkthrough';
import { ErrorPopup } from '@/components/ErrorPopup';

class ExternalScriptError extends Error {
  constructor(src: string) {
    super(`Error loading script: ${src}`);
    this.name = 'ExternalScriptError';
  }
}

let AppleID: any;
export const Register = () => {
  const { t } = useTranslation();
  const formik = FormikConfig();
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const authData = useSelector(authSelector);
  const [showWalkthrough, setShowWalkthrough] = useState(true);
  const [step, setStep] = useState(0);
  const [firstRender, setFirstRender] = useState(false);
  const [loading, SetLoading] = useState(true);
  const [location, setLocation] = useState('');
  const dispatch = useDispatch();
  const { redirectTo } = useAuth();
  useEffect(() => {
    if (typeof window !== "undefined") {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

      const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|LinkedInApp|Snapchat|TikTok/i.test(userAgent);

      if (isInAppBrowser) {
        dispatch(openModal({
          content: "It looks like you're trying to sign in through an in-app browser (like Instagram or TikTok). For your security, please open this page in a standard browser (such as Chrome or Safari)."
        }));
      } 
    }
  }, [dispatch]);
  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;

      return () => {
        document.body.style.background = ''; // Reset to default when unmounting
      };
    }, [color]);
  };
  useBodyBackground(isMobile ? '#F1F7FA' : 'linear-gradient(87.36deg, #D8E8F6 2.61%, #BBDFFF 98.24%)');
  useEffect(() => {
    setStep(0);
  }, []);

  const [appleReady, setAppleReady] = useState(false);
  const clientId = google_client_id;

  const decodeBase64 = (encodedString: any) => {
    return Buffer.from(encodedString, 'base64').toString('utf-8');
  };
  const [decodedUrl, setDecodedUrl] = useState({
    story: decodeBase64(
      Array.isArray(router?.query?.story_id) ? router.query?.story_id[0] : router.query?.story_id || '',
    ),
    email: decodeBase64(Array.isArray(router?.query?.guest) ? router.query?.guest[0] : router.query?.guest || ''),
    role: decodeBase64(Array.isArray(router?.query?.role) ? router.query?.role[0] : router?.query?.role || ''),
    notification: decodeBase64(
      Array.isArray(router?.query?.notification) ? router.query?.notification[0] : router.query?.notification || '',
    ),
    type: decodeBase64(Array.isArray(router?.query?.type) ? router.query?.type[0] : router.query?.type || ''),
  });

  UseFirstRender(() => {
    dispatch(registerUserView());
  }, [dispatch]);

  useEffect(() => {
    if (authData?.isAuth) {
      if (router.asPath?.includes('guest')) {
        dispatch(setGuest(router?.query?.role));
        dispatch(inviteAccepted({ story_id: router?.query?.story_id, role_name: router?.query?.role }));
        if (router?.query?.role === 'Story_Viewer') {
          router.push(`/app/story/${router?.query?.story_id}`);
        } else {
          router.push(`/app/story/${router?.query?.story_id}/memory/create`);
        }
      } else {
        router.push(redirectTo || '/app/home');
      }
    }
  }, [authData?.isAuth]);

  useEffect(() => {
    if (authData?.isAuth) {
      if (router.asPath?.includes('guest')) {
        if (decodedUrl?.email == authData?.user?.email) {
          dispatch(inviteAccepted({ story_id: decodedUrl?.story, role_name: decodedUrl?.role }));
          // dispatch(actualStory(decodedUrl.story));
          // dispatch(setGuest(decodedUrl?.role));
          // if (decodedUrl?.notification.length > 0) dispatch(deleteNotification(decodedUrl?.notification));
          // if (decodedUrl?.role === 'Story_Viewer') {
          //   router.push(`/app/story/${decodedUrl?.story}`);
          // } else {
          //   router.push(`/app/story/${decodedUrl?.story}/memory/create`);
          // }
        } else {
          router.push('/app/home');
        }
      } else {
        router.push('/app/home');
      }
    }
  }, [authData?.isAuth]);

  UseFirstRender(() => {
    if (!authData?.isAuth) {
      dispatch(loginUserView());
    }
  }, [authData?.isAuth]);

  useEffect(() => {
    const referralCode = router?.query?.referral_code;
    if (referralCode) {
      localStorage.setItem('referral_code', referralCode as string);
    }
  }, [router.query]);

  const handleSubmit = (data: any) => {
    data.email = data.email.toLowerCase();
    const { confirm_password, ...formValues } = data;
    if (router?.asPath?.includes('invitation')) {
      dispatch(registerUser({ ...formValues, invitation: router?.query?.invitation }));
    } else {
      dispatch(registerUser({ ...formValues }));
    }
  };

  const handleOnTouched = (key: string) => setTouched({ ...touched, [key]: true });

  const {
    values,
    handleSubmit: formikSubmit,
    handleChange,
    errors,
    touched,
    setTouched,
    dirty,
    isValid,
    setFieldValue,
  } = FormikConfig(handleSubmit);

  useEffect(() => {
    const storedReferralCode = localStorage.getItem('referral_code');
    if (storedReferralCode) {
      setFieldValue('referralCode', storedReferralCode);
    }
  }, [setFieldValue]);

  const WALKTHROUGH_KEY = 'walkthrough_shown';

  useEffect(() => {
    SetLoading(true);
    const hasSeenWalkthrough = localStorage.getItem(WALKTHROUGH_KEY);
    if (!hasSeenWalkthrough) {
      setShowWalkthrough(true);
      setTimeout(() => {
        SetLoading(false);
      }, 1200);
    } else {
      setShowWalkthrough(false);
      setTimeout(() => {
        SetLoading(false);
      }, 1200);
    }
  }, []);

  const handleWalkthroughComplete = () => {
    localStorage.setItem(WALKTHROUGH_KEY, 'true');
    setShowWalkthrough(false);
  };

  useEffect(() => {
    if (firstRender) {
      // Google script Init
      const google = require('gapi-script');
      const start = () => {
        google.gapi?.client.init({
          clientId: clientId,
          scope: 'email',
          cookiepolicy: 'single_host_origin',
        });
      };

      google.gapi?.load('client', start);

      let winType: any = window;
      winType.fbAsyncInit = function () {
        winType.FB.init({
          appId: face_client_id,

          xfbml: true,
          version: 'v19.0',
        });

        winType.FB.AppEvents.logPageView();
      };

      (function (d, s, id) {
        var js: any,
          fjs: any = d.getElementsByTagName(s)[0];
        if (d.getElementById(id)) {
          return;
        }
        js = d.createElement(s);
        js.id = id;
        js.src = 'https://connect.facebook.net/en_US/sdk.js';
        fjs?.parentNode?.insertBefore(js, fjs);
      })(document, 'script', 'facebook-jssdk');

      initializeAppleSDK();
    }
    if (!firstRender) setFirstRender(true);
  }, [firstRender]);

  function loadScript(src: any, { scriptId = '', insertBeforeAllTags = false } = {}) {
    return new Promise((resolve, reject) => {
      let script = document.createElement('script');
      script.id = scriptId;
      script.onload = resolve;
      script.onerror = (event: any) => {
        reject(new ExternalScriptError(event.target.src));
      };
      script.src = src;

      if (insertBeforeAllTags) {
        let firstScriptTagInDocument = document.getElementsByTagName('script')[0];

        firstScriptTagInDocument.parentNode &&
          firstScriptTagInDocument.parentNode.insertBefore(script, firstScriptTagInDocument);
      } else {
        document.body.appendChild(script);
      }
    });
  }

  const initializeAppleSDK = async () => {
    try {
      await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js');
  
      AppleID = (window as any).AppleID; // ✅ Fix: assign global AppleID
  
      setAppleReady(true);
  
      AppleID.auth.init({
        clientId: 'memvy-app-prod-service',
        scope: 'name email',
        redirectURI: 'https://memvy.com/app/register',
        state: 'initialState',
        usePopup: true,
      });
    } catch (error) {
      console.error('Error loading Apple SDK:', error);
    }
  };

  const appleResponse = (response: any) => {
    if (!response.error) {
      if (router?.asPath?.includes('invitation')) {
        dispatch(loginApple({ ...response, invitation: router?.query?.invitation, location: location }));
      } else {
        dispatch(loginApple({ ...response, location: location }));
      }
    }
  };
  const validateIsFaceLogged = () => {
    return new Promise((resolve) => {
      let winType: any = window;
      winType?.FB?.getLoginStatus(function (response: any) {
        if (response?.status === 'connected') {
          if (router?.asPath?.includes('invitation')) {
            dispatch(loginFacebook({ ...response?.authResponse, invitation: router?.query?.invitation }));
          } else {
            dispatch(loginFacebook(response?.authResponse));
          }
          resolve(true);
        } else if (response?.status === 'unknown') {
          resolve(false);
        } else {
          resolve(false);
        }
      });
    });
  };

  const initGoogleLogin = useGoogleLogin({
    scope: 'email',
    onSuccess: (tokenResponse: any) => {
      if (router?.asPath?.includes('invitation')) {
        dispatch(loginGoogle({ ...tokenResponse, invitation: router?.query?.invitation }));
      } else {
        dispatch(loginGoogle(tokenResponse));
      }
    },

    onError: (error: any) => {},
  });
  const handleFaceLogin = async () => {
    let winType: any = window;

    const isLogged = await validateIsFaceLogged();

    if (!isLogged) {
      winType?.FB?.login((response: any) => {
        if (response?.authResponse) {
          if (router?.asPath?.includes('invitation')) {
            dispatch(loginFacebook({ ...response?.authResponse, invitation: router?.query?.invitation }));
          } else {
            dispatch(loginFacebook(response?.authResponse));
          }
        } else {
          console.log('User cancelled login or did not fully authorize.');
        }
      });
    }
    return null;
  };

  UseFirstRender(() => {
    if (typeof window !== 'undefined') {
      let winType: any = window;
      setLocation(winType?.location?.href);
    }
  }, []);

  UseFirstRender(() => {
    if (location) {
      initializeAppleSDK();
    }
  }, [location]);

  const SocialLoginStep = ({ onNext }: any) => (
    <Box textAlign='center'>
      <Box mb={2}>
        <Image src='/images/logo-new.svg' width={66} height={34} alt='memvy' />
      </Box>
      <Typography mb={1} color='black' fontSize={'1.5rem'}>
        Log in or Sign up
      </Typography>
      <MuiButton
        variant='outlined'
        width={'100%'}
        method={() => initGoogleLogin()}
        sx={{ my: 1, py: 4, borderRadius: '0.4rem', borderColor: '#77BCE5', color: '#000' }}>
        {' '}
        <Image src='/images/google.svg' width={24} height={24} alt='google' />
        <Typography ml={1} color='#000' fontSize={'1rem'}>
          Continue with Google
        </Typography>
      </MuiButton>
      <AppleLogin
        clientId='memvy-app-prod-service'
        redirectURI='https://memvy.com/app/register'
        usePopup={appleReady ? true : false}
        callback={appleResponse}
        scope='name email'
        responseMode='form_post'
        state='SignInUserAuthenticationRequest'
        render={(renderProps: any) => (
          <MuiButton
            variant='outlined'
            width={'100%'}
            method={renderProps.onClick}
            sx={{ my: 1, py: 4, borderRadius: '0.4rem', borderColor: '#77BCE5', color: '#000' }}>
            <Image src='/images/appleblack.svg' width={24} height={24} alt='apple' />
            <Typography ml={1} color='#000' fontSize={'1rem'}>
              Continue with Apple
            </Typography>
          </MuiButton>
        )}
      />

      <MuiButton
        variant='outlined'
        width={'100%'}
        method={handleFaceLogin}
        sx={{ my: 1, py: 4, borderRadius: '0.4rem', borderColor: '#77BCE5', color: '#000' }}>
        <Image src='/images/facebook.svg' width={24} height={24} alt='facebook' />
        <Typography ml={1} color='#000' fontSize={'1rem'}>
          Continue with Facebook
        </Typography>
      </MuiButton>
      <Grid container alignItems='center' sx={{ width: '100%', my: 1 }}>
        <Grid item xs>
          <Divider sx={{ borderBottom: '1px solid', borderColor: palette.gray }} />
        </Grid>
        <Grid item px={4}>
          <Typography textAlign='center' color={palette.gray} variant={isMobile ? 'body2' : 'body1'}>
            {t('or')}
          </Typography>
        </Grid>
        <Grid item xs>
          <Divider sx={{ borderBottom: '1px solid', borderColor: palette.gray }} />
        </Grid>
      </Grid>
      <Box display={'flex'} justifyContent={'space-between'} gap={2}>
        <MuiButton
          variant='contained'
          width={!isMobile ? '100%' : '100%'}
          method={() => {
            onNext();
          }}
          sx={{ my: 1, py: 4, borderRadius: '0.5rem', backgroundColor: '#212D6D', color: '#fff' }}>
          <Image src='/icons/Envelope.svg' width={24} height={24} alt='email' />
          <Typography ml={'18px'} fontSize={'1rem'}>
            Sign Up{' '}
          </Typography>
        </MuiButton>
        <MuiButton
          variant='contained'
          width={!isMobile ? '100%' : '100%'}
          method={() => router.push('/app/login')}
          sx={{ my: 1, py: 4, borderRadius: '0.5rem', backgroundColor: '#212D6D', color: '#fff' }}>
          <Image src='/icons/Envelope.svg' width={24} height={24} alt='email' />

          <Typography ml={'18px'} fontSize={'1rem'}>
            Log In{' '}
          </Typography>
        </MuiButton>
      </Box>
      {step === 0 && (
        <Box width={'90%'} display={'flex'} margin={'0 auto'} mt={2}>
          <Typography color='#5C6372' fontSize={'0.625rem'}>
            By continuing with Google, Apple, Facebook, or email, you agree to our User{' '}
            <Link style={{ textDecoration: 'underline' }} href='/terms'>
              {' '}
              Terms of Service{' '}
            </Link>{' '}
            and{' '}
            <Link style={{ textDecoration: 'underline' }} href='/privacy'>
              {' '}
              Privacy Policy{' '}
            </Link>
            .
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box
        display={'flex'}
        height={isMobile ? '' : '100vh'}
        width={'100%'}
        zIndex={1}
        flexDirection={'column'}
        bgcolor={isMobile ? '#F1F7FA' : ''}
        position={'relative'}
        padding={'0 1rem'}
        // paddingTop={isMobile ? '46px' : ''}
        justifyContent={isMobile ? 'flex-start' : 'center'}
        alignItems={'center'}
        overflow={'auto'}>
        {!isMobile && (
          <Image
            src={isMobile ? '/images/new_thread.svg' : '/images/thread.svg'}
            style={{
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
              margin: 'auto',
              zIndex: -1,
            }}
            alt='background'
            fill
            priority
            sizes='100%'
            quality={80}
          />
        )}
        {!(step === 0 && isMobile) && (
          <IconButton
            disableRipple
            onClick={() => router.push('/')}
            sx={{ position: 'absolute', top: '1rem', left: isMobile ? '1rem' : '20rem' }}>
            <Image width={24} height={24} alt='back' color='black' src='/icons/left-arrow-black.svg' />
            {!isMobile && (
              <Typography fontSize={'1rem'} color='#212d6d' marginLeft={'0.6rem'}>
                Back to Memvy.com
              </Typography>
            )}
          </IconButton>
        )}
        {loading ? (
          <Box width={'100%'} height={'100vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <CircularProgress sx={{ color: palette.faintGray }} />
          </Box>
        ) : (
          <>
            {showWalkthrough && !authData?.isAuth ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  padding: '1.8rem',
                  height: isMobile ? '100vh' : 'auto',
                  borderRadius: '0.5rem',
                  margin: 'auto',
                }}>
                <WalkThrough handleWalkthroughComplete={handleWalkthroughComplete} />
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '33rem',
                  height: isMobile ? '100vh' : 'auto',
                  backgroundColor: isMobile ? 'transparent' : '#F1F7FA',
                  padding: '1.8rem',
                  borderRadius: '0.5rem',
                  margin: 'auto',
                }}>
                {step === 0 && <SocialLoginStep onNext={() => setStep(1)} />}
                {step === 1 && (
                  <EmailPasswordStep formik={formik} onNext={() => setStep(2)} onBack={() => setStep(0)} />
                )}
                {step === 2 && <UserDetailsStep formik={formik} onBack={() => setStep(1)} />}
              </Box>
            )}
          </>
        )}
      </Box>
      <ErrorPopup/>
    </>
  );
};
